import {
	ResourcesFree,
	ResourcesState,
	TResourceState,
} from 'entities/hallmap/model/types';
import { useCallback, useEffect } from 'react';
import { Game, Table } from 'shared/lib/core';
import { isResourceState } from '../helpers/resources/isResourceState';
import { useAppSelector } from 'shared/lib/store';
import {
	selecResourcesFree,
	selectResourcesState,
} from 'entities/hallmap/model/selectors';
import { useOrderResources } from './useOrderResources';
import { UpdateHallmapTablesProps } from '../types';
import { formatTime, getDiffBetweenDates, minutesToTime } from 'shared/lib/time';
import { Order } from 'entities/orders';
import { format, isAfter, isPast } from 'date-fns';
import { selectSelectedTimeslot } from 'entities/toolbar/timeslots';
import { COLORS } from 'shared/lib/core/constants';

export const useUpdateHallmapTables = ({
	game,
	order,
	mode,
}: UpdateHallmapTablesProps) => {
	const resourcesState = useAppSelector(selectResourcesState);
	const resourcesFree = useAppSelector(selecResourcesFree);
	const timeslot = useAppSelector(selectSelectedTimeslot);

	const { getResourcesIdsFormOrder } = useOrderResources(order);

	const resetTableState = (table: Table) => {
		table.setStatus('free');
		table.setBooked(0);
		table.setDiagram(0, 0);
		table.setTimeLabel('')
		table.setGuestName('');
	};

	const updateTableDiagrams = (
		table: Table,
		state: TResourceState,
		start: string,
		end: string,
		duration: number,
		order: Order,
		timeslot: string,
	) => {
		const currentDate = new Date();
		const dateStart = new Date(start);
		const isStarted = Boolean(order?.appointment?.dt_visit_start);
		const dateVisitStart = new Date(order?.appointment?.dt_visit_start);
		const dateEnd = new Date(end);

		if (state === 'busy' && order?.status === 'appointed') {
			if (timeslot && currentDate.getTime() < new Date(timeslot).getTime()) {
				const date = formatTime({ time: order?.dt_start });
				const differenceObj = getDiffBetweenDates(new Date(timeslot), dateStart);
				if (differenceObj && differenceObj.minutes) {
					let progress = differenceObj.minutes / duration;
					if (progress > 1) {
						progress = 1;
					} else if (progress < 0) {
						progress = 0;
					}
	
					table.setDiagramColors('white', 'white')
					table.setDiagram(1, progress);
					table.changeAppearence(
						COLORS.TABLE.BUSY_APPOINTED.BG,
						table.isSelected ? COLORS.SELECTED : COLORS.BORDER,
						COLORS.SEATS.ACTIVE
					)

					const isPrev = new Date(timeslot).getTime() > new Date(order.dt_start).getTime();
					const isNext = new Date(timeslot).getTime() < new Date(order.dt_start).getTime();

					if (date) {
						table.setTimeLabel(`${isPrev ? '◄ ' : ''}${date.time}${isNext ? ' ►' : ''}`)
					}
				}

				return;
			}
			
			return;
		}

		if(state === 'busy') {
			if (timeslot && currentDate.getTime() < new Date(timeslot).getTime()) {
				const differenceObj = getDiffBetweenDates(new Date(timeslot), isStarted ? dateVisitStart : dateStart);
				if (differenceObj && differenceObj.minutes) {
					let progress = differenceObj.minutes / duration;
					if (progress > 1) {
						progress = 1;
					} else if (progress < 0) {
						progress = 0;
					}
	
					table.setDiagramColors('white', 'white')
					table.setDiagram(1, progress);

					// if (state === 'ending') {
					// 	table.setBooked(duration - differenceObj?.minutes)
					// }
				}

				return;
			}
			

			if (isAfter(dateStart, dateVisitStart)) {
				const diff = getDiffBetweenDates(currentDate, dateVisitStart);
				const minutesDiff = getDiffBetweenDates(dateStart, dateVisitStart);

				if (diff && diff.minutes && minutesDiff?.minutes) {
					let progress = diff.minutes / (duration + minutesDiff?.minutes);
					if (progress > 1) {
						progress = 1;
					} else if (progress < 0) {
						progress = 0;
					}	

					table.setDiagramColors('white', 'white')
					table.setDiagram(1, progress);

					// if (state === 'ending') {
					// 	table.setBooked(duration + minutesDiff?.minutes - diff.minutes)
					// }
				}

				return;
			}

			if (currentDate.getTime() >= dateStart.getTime()) {
				const differenceObj = getDiffBetweenDates(currentDate, isStarted ? dateVisitStart : dateStart);
				if (differenceObj && differenceObj.minutes) {
					let progress = differenceObj.minutes / duration;
					if (progress > 1) {
						progress = 1;
					} else if (progress < 0) {
						progress = 0;
					}
	
					table.setDiagramColors('white', 'white')
					table.setDiagram(1, progress);

					// if (state === 'ending') {
					// 	table.setBooked(duration - differenceObj?.minutes)
					// }
				}
			}
		}

		// if(state === 'exceeding') {
		// 	if (currentDate.getTime() >= dateEnd.getTime()) {
		// 		const differenceObj = getDiffBetweenDates(currentDate, dateEnd);
		// 		if (differenceObj && differenceObj.minutes) {
		// 			let progress = (differenceObj.minutes * 100 / 30) / 100;

		// 			if (progress > 1) {
		// 				progress = 1;
		// 			} else if (progress < 0) {
		// 				progress = 0;
		// 			}
	
		// 			table.setDiagramColors('#F75D61', 'white')
		// 			table.setDiagram(2, progress);
		// 		}
		// 	}
		// }
		if(state === 'exceeding') {
      const time = format(dateEnd, 'HH:mm');
      table.setTimeLabel(`◄ ${time}`);
		}
	};

	const updateResourceState = useCallback(
		(resource: ResourcesState, table: Table) => {
			const guestName = resource.order?.person?.name;
			const booked = resource.order?.participants_count;
			const start = resource.order?.dt_start;
			const end = resource.order?.dt_end;
			const duration = resource.order?.duration;
			const state = resource.state;
			const orderStatus = resource.order?.status;
			table.setOrder(resource.order);

			resetTableState(table);

			if (orderStatus !== 'visit_ended' && orderStatus !== 'cancelled') {
				table.setActive(resource.is_active);
				table.setStatus(state);

				if (booked) {
					if (state === 'waiting' || state === 'delay')
					if (!isPast(resource.order.dt_start)) {
						table.setBooked(booked, 'black');
					} else {
						table.setBooked?.(booked, 'white');
					}
				}

				if (guestName) {
					table.setGuestName(guestName);
				}

				updateTableDiagrams(table, state, start, end, duration, resource.order, timeslot);
			}
		},
		[timeslot],
	);

  const updateFreeResourceState = (resource: ResourcesFree, table: Table) => {
    table.setActive(resource.is_free);
    table.setTimeLabel('');
    
    if (resource.person_name) {
      table.setGuestName(resource.person_name)
    } else {
      table.setGuestName('')
    }

    if (resource.is_relevant) {
      table.setStatus('relevant');
    } else {
      table.setStatus('free');
    }

    if (resource.is_free) {
      const after = resource.after;
      let timeLabel = '';

      if (after < 60) {
        timeLabel = `${after}м ►`;
      } else if (after >= 60 && after < 120) {
        timeLabel = `${minutesToTime(after)} ►`;
      } else {
        timeLabel = '2ч+ ►';
      }

      table.setTimeLabel(timeLabel)
    }
  }

	const updateTableStatus = useCallback(
		(game: Game, resources: (ResourcesState | ResourcesFree)[]) => {
			resources.forEach(resource => {
				const table: Table = game.resourcesMapByName.get(resource.name);
				if (table) {
					if (isResourceState(resource)) {
						updateResourceState(resource, table);
					} else {
            updateFreeResourceState(resource, table);
					}
				}
			});
		},
		[updateResourceState],
	);

	useEffect(() => {
		if (game && mode === 'static') {
			updateTableStatus(game, resourcesState);
		}
	}, [resourcesState, game, mode, updateTableStatus]);

	useEffect(() => {
		if (mode === 'select' && resourcesFree.length && game) {
			game.resources.forEach(resource => {
				resource.setActive(false);
			});

			updateTableStatus(game, resourcesFree);
		}
	}, [
		resourcesFree,
		game,
		mode,
		getResourcesIdsFormOrder,
		updateTableStatus,
	]);
};
